/**
 * PORTED — RO-3-11 / #44304.
 * Origin: `excellent/app/database/migrations/_client.tsx`.
 *
 * Changes, and only these:
 *   `"use client"`      -> dropped (inert in this host)
 *   `MigrationRow`      -> `AppliedMigrationDto` from the contract
 *
 * The second one is the point of the port rather than a tidy-up: the origin
 * declared its own row interface because a Next server page handed it plain data
 * it had just read from `_migrations` itself. Here the rows arrive over
 * `schema.migrations`, so the contract's `AppliedMigrationDto` IS the shape — one
 * declaration, validated by zod at the boundary, instead of two that agree by
 * inspection. The fields are identical.
 *
 * It stays a separate file (rather than folding into `database.tsx`) for the
 * reason its origin header gives: the sortable `DataTable` needs `sort`/`render`
 * functions, which is why it was an island in the first place. One consumer, so
 * it lives beside its route rather than being promoted to `packages/ui` — a block
 * earns promotion when a second consumer appears.
 */

import { DataTable, InfoHint, StatStrip, formatDateTime, type StatStripItem } from "@excellent/ui";
import type { AppliedMigrationDto } from "@excellent/ui/api/contracts/database/schema";

export function AppliedMigrationsTable({
  rows,
  stats,
}: {
  rows: AppliedMigrationDto[];
  stats: StatStripItem[];
}) {
  return (
    <DataTable
      title={
        <span className="flex items-center gap-2">
          Applied migrations
          <InfoHint label="About applied migrations" side="bottom" align="start">
            The canonical shipped schema, by product. Changes made in the
            explorer are live-only and appear in the change audit below — they
            are not written back to the migration source of truth.
          </InfoHint>
        </span>
      }
      count={rows.length}
      strongHeader
      summary={<StatStrip variant="table" items={stats} />}
      rows={rows}
      rowKey={(r) => `${r.product}:${r.version}`}
      initialSort={{ key: "product", dir: "asc" }}
      columns={[
        {
          key: "product",
          header: "Product",
          sort: (a, b) => a.product.localeCompare(b.product),
          render: (r) => r.product,
        },
        {
          key: "version",
          header: "Version",
          width: "w-24",
          sort: (a, b) => a.version - b.version,
          render: (r) => <span className="tabular-nums">v{r.version}</span>,
        },
        {
          key: "applied_at",
          header: "Applied at",
          width: "w-56",
          sort: (a, b) => a.applied_at.localeCompare(b.applied_at),
          render: (r) => (
            <span className="text-muted-foreground tabular-nums">
              {formatDateTime(r.applied_at)}
            </span>
          ),
        },
      ]}
    />
  );
}
